import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
  Chip,
  Button,
  IconButton
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import config from '../config';
import OrderCreationAttachments from './OrderCreationAttachments';
import EnhancedSearch from './common/EnhancedSearch';
import { createStructuredNote, parseNotesBlob } from '../utils/noteUtils';
import NoteCard from './notes/NoteCard';

const StationeryOrderForm = ({ formData, setFormData, organizations }) => {
  const { getToken, user } = useAuth();
  
  const [stationerySettings, setStationerySettings] = useState({
    marginPercentage: 25,
    artworkFee: 45,
    setupFee: 25,
    gstPercentage: 10
  });

  // Notes state - seed from any existing formData.notes blob (edit mode) so
  // previously saved notes show up split into individual NoteCard entries.
  const [notes, setNotes] = useState(() => parseNotesBlob(formData.notes, { idPrefix: 'note', noteType: 'general', createdBy: 'Staff' }));
  const [newNote, setNewNote] = useState('');

  // Attachments state
  const [tempAttachments, setTempAttachments] = useState([]);

  // Fetch stationery settings
  useEffect(() => {
    const fetchStationerySettings = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${config.apiUrl}/api/settings`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.data) {
            setStationerySettings({
              marginPercentage: parseFloat(data.data.stationeryMarginPercentage) || 25,
              artworkFee: parseFloat(data.data.stationeryArtworkFee) || 45,
              setupFee: parseFloat(data.data.stationerySetupFee) || 25,
              gstPercentage: parseFloat(data.data.stationeryGstPercentage) || 10
            });
          }
        }
      } catch (error) {
        console.error('Error fetching stationery settings:', error);
      }
    };

    fetchStationerySettings();
  }, [getToken]);

  // Calculate derived values
  const calculateValues = (updatedFormData = formData) => {
    const price = parseFloat(updatedFormData.price) || 0;
    const freightPrice = parseFloat(updatedFormData.freightPrice) || 0;
    const additionalArtworkHours = parseFloat(updatedFormData.additionalArtworkHours) || 0;
    
    // Calculate margin (25% of price)
    const margin = price * (stationerySettings.marginPercentage / 100);
    
    // Calculate artwork fee (additional hours * $45 + base $45)
    const artworkFee = stationerySettings.artworkFee + (additionalArtworkHours * stationerySettings.artworkFee);
    
    // Setup fee is fixed
    const setupFee = stationerySettings.setupFee;
    
    // Calculate subtotal before GST and discount
    const subtotalBeforeGst = price + freightPrice + margin + artworkFee + setupFee;
    
    // Calculate GST (10% of subtotal)
    const gst = subtotalBeforeGst * (stationerySettings.gstPercentage / 100);
    
    // Calculate discount
    const discountAmount = parseFloat(updatedFormData.discountAmount) || 0;
    const discountType = updatedFormData.discountType || '$';
    let discountValue = 0;
    
    if (discountAmount > 0) {
      if (discountType === '%') {
        discountValue = (subtotalBeforeGst + gst) * (discountAmount / 100);
      } else {
        discountValue = discountAmount;
      }
    }
    
    // Calculate total
    const total = subtotalBeforeGst + gst - discountValue;
    
    return {
      margin: margin.toFixed(2),
      artworkFee: artworkFee.toFixed(2),
      setupFee: setupFee.toFixed(2),
      gst: gst.toFixed(2),
      totalPrice: total.toFixed(2)
    };
  };

  // Update calculated values when relevant fields change
  useEffect(() => {
    if (formData.price || formData.freightPrice || formData.additionalArtworkHours || formData.discountAmount) {
      const calculatedValues = calculateValues();
      setFormData(prev => ({
        ...prev,
        margin: calculatedValues.margin,
        artworkFee: calculatedValues.artworkFee,
        setupFee: calculatedValues.setupFee,
        gst: calculatedValues.gst,
        totalPrice: calculatedValues.totalPrice
      }));
    }
  }, [
    formData.price,
    formData.freightPrice,
    formData.additionalArtworkHours,
    formData.discountAmount,
    formData.discountType,
    stationerySettings.marginPercentage,
    stationerySettings.artworkFee,
    stationerySettings.setupFee,
    stationerySettings.gstPercentage
  ]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };



  // Notes handlers
  const addNote = () => {
    if (newNote.trim()) {
      const userName = user?.name || user?.email || 'Staff';
      // Use the same "(date) User from Source: content" structured format used
      // everywhere else (offer creation, ad orders, notes dialogs) so every note
      // parses and displays consistently regardless of where it was added.
      const raw = createStructuredNote(newNote.trim(), userName, 'stationeryOrder', 'general');

      const note = {
        id: Date.now(),
        content: newNote.trim(),
        createdAt: new Date().toISOString(),
        createdBy: userName,
        raw
      };
      const updatedNotes = [...notes, note];
      setNotes(updatedNotes);
      setNewNote('');

      // Update formData to include all notes, preserving their structured form
      const allNotes = updatedNotes.map(n => n.raw || n.content).join('\n\n');
      setFormData(prev => ({
        ...prev,
        notes: allNotes
      }));
    }
  };

  const removeNote = (noteId) => {
    const updatedNotes = notes.filter(note => note.id !== noteId);
    setNotes(updatedNotes);

    const allNotes = updatedNotes.map(n => n.raw || n.content).join('\n\n');
    setFormData(prev => ({
      ...prev,
      notes: allNotes
    }));
  };

  // Handle attachment changes
  const handleAttachmentsChange = (attachments) => {
    setTempAttachments(attachments);
    // Update formData to include attachments
    setFormData(prev => ({
      ...prev,
      tempAttachments: attachments
    }));
  };

  // Filter organizations to show all (both schools and businesses for stationery)
  const availableOrganizations = organizations.filter(org => org.isActive);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Stationery Order Information
      </Typography>

      {/* Organisation Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Organisation Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <EnhancedSearch
              label="Search Organisation"
              placeholder="Type to search organizations..."
              endpoint="/organizations/search"
              onSelect={(selectedOrg) => {
                if (selectedOrg) {
                  setFormData(prev => ({
                    ...prev,
                    organization_id: selectedOrg.id,
                    business_number: selectedOrg.id.toString(),
                    business_name: selectedOrg.name || '', // Keep this for validation
                    selectedOrganizationName: selectedOrg.name || ''
                  }));
                } else {
                  // Clear organization fields when selection is removed
                  setFormData(prev => ({
                    ...prev,
                    organization_id: '',
                    business_number: '',
                    business_name: '',
                    selectedOrganizationName: ''
                  }));
                }
              }}
              getOptionLabel={(option) => option.name || ''}
              value={formData.selectedOrganizationName ? { name: formData.selectedOrganizationName, id: formData.organization_id } : null}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="ID"
              name="business_number"
              value={formData.business_number || ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Product Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Product Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Product Type"
              name="productType"
              value={formData.productType || ''}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity || ''}
              onChange={handleInputChange}
              inputProps={{ min: 1 }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Size"
              name="size"
              value={formData.size || ''}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Sides</InputLabel>
              <Select
                name="sides"
                value={formData.sides || ''}
                onChange={handleInputChange}
                label="Sides"
              >
                <MenuItem value="">
                  <em>Select sides</em>
                </MenuItem>
                <MenuItem value="1">Single Sided</MenuItem>
                <MenuItem value="2">Double Sided</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Stock"
              name="stock"
              value={formData.stock || ''}
              onChange={handleInputChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Finish"
              name="finish"
              value={formData.finish || ''}
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Pricing Information */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Pricing Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={formData.price || ''}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Freight Price"
              name="freightPrice"
              type="number"
              value={formData.freightPrice || ''}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Additional Artwork Hours"
              name="additionalArtworkHours"
              type="number"
              value={formData.additionalArtworkHours || ''}
              onChange={handleInputChange}
              inputProps={{ min: 0, step: 0.25 }}
              helperText="Enter hours for additional artwork"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Calculated Fields - Read Only */}
        <Typography variant="h6" gutterBottom color="secondary">
          Calculated Fees (Read Only)
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`Margin (${stationerySettings.marginPercentage}%)`}
              value={`$${formData.margin || '0.00'}`}
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Artwork Fee"
              value={`$${formData.artworkFee || '0.00'}`}
              InputProps={{ readOnly: true }}
              variant="filled"
              helperText={`Base $${stationerySettings.artworkFee} + hours`}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Setup Fee"
              value={`$${formData.setupFee || '0.00'}`}
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label={`GST (${stationerySettings.gstPercentage}%)`}
              value={`$${formData.gst || '0.00'}`}
              InputProps={{ readOnly: true }}
              variant="filled"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Discount */}
        <Typography variant="h6" gutterBottom color="primary">
          Discount
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                name="discountType"
                value={formData.discountType || '$'}
                onChange={handleInputChange}
              >
                <FormControlLabel value="$" control={<Radio />} label="Dollar ($)" />
                <FormControlLabel value="%" control={<Radio />} label="Percentage (%)" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Discount Amount"
              name="discountAmount"
              type="number"
              value={formData.discountAmount || ''}
              onChange={handleInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.discountType === '%' ? '%' : '$'}
                  </InputAdornment>
                ),
              }}
              inputProps={{ min: 0, step: formData.discountType === '%' ? 1 : 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Total Price"
              value={`$${formData.totalPrice || '0.00'}`}
              InputProps={{ readOnly: true }}
              variant="filled"
              sx={{ 
                '& .MuiFilledInput-root': { 
                  backgroundColor: 'primary.50',
                  fontSize: '1.2rem',
                  fontWeight: 'bold'
                }
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Notes */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          Notes
        </Typography>
        
        {/* Add new note */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            label="Add Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            multiline
            rows={3}
            placeholder="Enter a note for this stationery order..."
            onKeyPress={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                addNote();
              }
            }}
          />
          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={addNote}
              disabled={!newNote.trim()}
              size="small"
            >
              Add Note
            </Button>
          </Box>
        </Box>

        {/* Display existing notes */}
        {notes.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Order Notes ({notes.length})
            </Typography>
            {notes.map((note) => (
              <Box key={note.id} sx={{ position: 'relative', mb: 2 }}>
                <NoteCard note={note} color="primary" bgColor="grey.50" borderColor="grey.200" />
                <IconButton
                  size="small"
                  onClick={() => removeNote(note.id)}
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {notes.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No notes added yet. Add a note above to get started.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Attachments */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom color="primary">
          <CloudUploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Attachments
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        <OrderCreationAttachments
          attachments={tempAttachments}
          onAttachmentsChange={handleAttachmentsChange}
          maxFiles={10}
        />
      </Paper>
    </Box>
  );
};

export default StationeryOrderForm;
